import React, { useCallback } from 'react';
import { motion } from 'motion/react';
import { Wifi, Bluetooth, Moon, Volume2, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '@/contexts/AppContext';
import { voiceDisplayName } from '../VoicePicker';
import { systemService } from '@/services/systemService';
import { APP_VERSION } from '@/lib/version';

export function ControlCenter({ isOpen, onClose, t, brightness, setBrightness, volume, setVolume, lang, setLang, toggleWindow }: {
  isOpen: boolean;
  onClose: () => void;
  t: any;
  brightness: number;
  setBrightness: (v: number) => void;
  volume: number;
  setVolume: (v: number) => void;
  lang: 'en' | 'zh';
  setLang: (l: 'en' | 'zh') => void;
  toggleWindow: (id: string) => void;
}) {
  const { selectedVoiceId, unreadCount } = useApp();

  const handleHealthCheck = useCallback(async () => {
    try {
      const r = await fetch('/api/health');
      if (r.ok) toast.info(t.serverOnline);
      else toast.info(t.serverDegraded);
    } catch {
      toast.error(t.serverOffline);
    }
  }, [t.serverOnline, t.serverDegraded, t.serverOffline]);

  const handleBluetooth = useCallback(() => {
    toast.info(t.bluetoothRequiresDesktop);
  }, [t.bluetoothRequiresDesktop]);

  const handleLangEn = useCallback(() => setLang('en'), [setLang]);
  const handleLangZh = useCallback(() => setLang('zh'), [setLang]);

  const handleBrightness = useCallback((e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const v = Math.min(100, Math.max(0, Math.round(percent * 100)));
    setBrightness(v);
    systemService.setBrightness(v);
  }, [setBrightness]);

  const handleVolume = useCallback((e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const v = Math.min(100, Math.max(0, Math.round(percent * 100)));
    setVolume(v);
    systemService.setVolume(v);
  }, [setVolume]);

  const handleOpenVoice = useCallback(() => {
    toggleWindow('voice');
    onClose();
  }, [toggleWindow, onClose]);

  const handleOpenNotifications = useCallback(() => {
    toggleWindow('notifications');
    onClose();
  }, [toggleWindow, onClose]);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      className="fixed top-12 right-6 w-80 glass-dark rounded-[2.5rem] p-6 z-[100] shadow-[0_30px_70px_rgba(0,0,0,0.7)] border border-white/10 backdrop-blur-3xl"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xs font-black uppercase tracking-widest text-white/40">{t.nexusControl || '中枢控制'}</h3>
        <div className="flex bg-white/5 p-1 rounded-xl">
          <button onClick={handleLangEn} className={`px-3 py-1 text-xs font-black uppercase tracking-widest rounded-lg transition-all ${lang === 'en' ? 'bg-white text-black' : 'text-white/40'}`}>EN</button>
          <button onClick={handleLangZh} className={`px-3 py-1 text-xs font-black uppercase tracking-widest rounded-lg transition-all ${lang === 'zh' ? 'bg-white text-black' : 'text-white/40'}`}>ZH</button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="col-span-1 bg-white/5 rounded-2xl p-4 flex items-center justify-center gap-3">
          <button onClick={handleHealthCheck} className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white active:scale-95 transition-transform" title={t.wifi}><Wifi size={18} /></button>
          <button onClick={handleBluetooth} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/40 active:scale-95 transition-transform" title={t.bluetooth}><Bluetooth size={18} /></button>
        </div>
        <div className="col-span-1 bg-white/5 rounded-[1.5rem] p-5 flex flex-col justify-between">
          <div className="space-y-2">
            <div className="flex justify-between items-center text-xs font-bold text-white/40 uppercase">
              <span>{t.display || '显示'}</span>
              <Moon size={12} className="text-blue-300/70" />
            </div>
            <div className="h-4 w-full bg-white/5 rounded-full relative group cursor-pointer" onClick={handleBrightness}>
              <motion.div animate={{ width: `${brightness}%` }} className="h-full bg-celestial-saturn rounded-full" />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center text-xs font-bold text-white/40 uppercase">
              <span>{t.sound || '声音'}</span>
              <Volume2 size={12} />
            </div>
            <div className="h-4 w-full bg-white/5 rounded-full relative group cursor-pointer" onClick={handleVolume}>
              <motion.div animate={{ width: `${volume}%` }} className="h-full bg-celestial-saturn rounded-full" />
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2 mb-6">
        <span className="text-xs font-black text-white/45 uppercase tracking-widest px-2">{t.aiCore || 'AI 核心'}</span>
        <div className="space-y-1">
          <button onClick={handleOpenVoice} className="w-full flex items-center justify-between p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-colors">
            <div className="flex items-center gap-2">
              <Volume2 size={14} className="text-pink-400" />
              <span className="text-xs font-bold text-white/70">{t.voiceLabel || '语音'}</span>
            </div>
            <span className="text-xs font-black text-pink-400 truncate max-w-[100px]">{selectedVoiceId ? voiceDisplayName(selectedVoiceId) : (t.defaultLabel || '默认')}</span>
          </button>

          <button onClick={handleOpenNotifications} className="w-full flex items-center justify-between p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-colors">
            <div className="flex items-center gap-2">
              <Bell size={14} className="text-amber-400" />
              <span className="text-xs font-bold text-white/70">{t.notificationsLabel || '通知'}</span>
            </div>
            <span className="text-xs font-black text-amber-400">{unreadCount} {t.unread || '未读'}</span>
          </button>
        </div>
      </div>
      <div className="mt-6 pt-6 border-t border-white/5 flex items-center justify-between font-sans">
        <span className="text-xs font-bold text-white/45 tracking-widest uppercase">{t.desktopVersion || `小枫 OS ${APP_VERSION}`}</span>
        <button onClick={onClose} className="text-xs font-black text-celestial-saturn hover:underline uppercase tracking-widest">{t.closeNexus || '关闭中枢'}</button>
      </div>
    </motion.div>
  );
}


